import { getLogger } from "@expert/logging";
import { immerable } from "immer";

import type { Task } from "../agent/task";
import type { WrappingState } from "../agent/types";
import type { Session, SessionChangeReason, SessionKind, SessionMetadata, TaskId } from "./types";

const logger = getLogger({
    module: "SessionInstance",
});

export class SessionInstance implements Pick<Session, keyof Session> {
    [immerable] = true;

    id: string;

    kind: SessionKind;
    startedTimestamp: number;
    endedTimestamp?: number;

    // Chronological history of tasks that have been active during this session
    tasks: Map<TaskId, Task>;
    private rehydratedTasks: Map<TaskId, Task> = new Map();

    metadata: SessionMetadata;

    wrappingState: WrappingState | null;

    constructor(sessionData: Omit<Session, "toLog" | "currentTask">) {
        this.id = sessionData.id;
        this.kind = sessionData.kind;
        this.startedTimestamp = sessionData.startedTimestamp;
        this.endedTimestamp = sessionData.endedTimestamp;
        this.tasks = sessionData.tasks;

        this.metadata = sessionData.metadata;

        this.wrappingState = sessionData.wrappingState ?? null;
    }

    /** The current active task for a session that is not completed. Otherwise undefined */
    public get currentTask() {
        const latestTask = Array.from(this.tasks.values()).at(-1);
        // A  completed task is no longer active, and there is effectively no "current" task
        // This logic used to be downstream, but to keep it DRY, we're moving it here
        if (latestTask && latestTask.status === "completed") {
            return undefined;
        }

        return latestTask;
    }

    // We hide the rehydrated tasks after rehydration and then restore them once the SDK is initialized
    // This was a design ask so we avoid exposing tasks to session consumers before we have synced state from the vendor
    hideRehydratedTasks() {
        this.rehydratedTasks = new Map(this.tasks);
        this.tasks.clear();
    }

    restoreHydratedTasks() {
        this.tasks = new Map(this.rehydratedTasks);
        this.rehydratedTasks.clear();
    }

    /** @returns all tasks, even ones that are not yet rehydrated */
    getAllTasks() {
        return new Map([...this.rehydratedTasks, ...this.tasks]);
    }

    /*
    TODO:// upsert or something (one function for both)
    */
    addTask(task: Task) {
        this.tasks.set(task.id, task);
    }
    replaceTask(task: Task) {
        this.tasks.set(task.id, task);
    }
    /*
    TODO:// ====================
    */

    endSession(reason: SessionChangeReason) {
        // This _shouldn't_ happen, and is likely an error. However, all potential cases are not yet enumerated, so we don't throw here.
        if (reason === this.metadata.startReason) {
            logger.info({ sessionId: this.id, reason }, "Session end reason is the same as the start reason");
        }

        this.metadata.endReason = reason;
        this.endedTimestamp = Date.now();
        this.metadata.status = "ended";
    }

    /**
     * This function extracts from session simple details for logging.
     * It uses explicit properties name so we can destructure the object in the root level of the log record
     * @returns Session details for logging
     */
    public toLog(): Record<string, unknown> {
        return {
            sessionId: this.id,
            sessionKind: this.kind,
            sessionStartTime: this.startedTimestamp,
            sessionEndTime: this.endedTimestamp,
            sessionStartReason: this.metadata.startReason,
            sessionEndReason: this.metadata.endReason,
        };
    }
}
